import { useEffect, useId, useRef, useState } from "react";
import type { PublicCredentialLimits } from "@mph/launch-control";

import {
  AccountRequestError,
  accountErrorMessage,
  getProjectPublicApiSettings,
  updateProjectPublicApiSettings,
  type ProjectPublicApiSettings,
} from "../../lib/account-client";

export interface ProjectApiSettingsApi {
  getProjectPublicApiSettings: typeof getProjectPublicApiSettings;
  updateProjectPublicApiSettings: typeof updateProjectPublicApiSettings;
}

export const projectApiSettingsApi: ProjectApiSettingsApi = {
  getProjectPublicApiSettings,
  updateProjectPublicApiSettings,
};

export function ProjectApiSettings({
  workspaceId,
  projectId,
  api = projectApiSettingsApi,
}: {
  workspaceId: string;
  projectId: string;
  api?: ProjectApiSettingsApi;
}) {
  const errorId = useId();
  const [settings, setSettings] = useState<ProjectPublicApiSettings | null>(null);
  const [limits, setLimits] = useState<PublicCredentialLimits | null>(null);
  const [approvalActionId, setApprovalActionId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const approvalConfirmRef = useRef<HTMLButtonElement>(null);
  const approvalReturnRef = useRef<HTMLButtonElement>(null);

  const replace = (next: ProjectPublicApiSettings) => {
    setSettings(next);
    setLimits(next.limits.configured);
  };
  const load = async () => {
    const next = await api.getProjectPublicApiSettings(workspaceId, projectId);
    replace(next);
    return next;
  };

  useEffect(() => {
    let active = true;
    setSettings(null);
    setError(null);
    void api.getProjectPublicApiSettings(workspaceId, projectId)
      .then((next) => { if (active) replace(next); })
      .catch((cause: unknown) => {
        if (active) setError(accountErrorMessage(cause, "Doorway could not load public API policy settings."));
      });
    return () => { active = false; };
  }, [api, loadAttempt, projectId, workspaceId]);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (approvalActionId) approvalConfirmRef.current?.focus(); }, [approvalActionId]);

  const apply = async (
    operation: Parameters<typeof updateProjectPublicApiSettings>[2],
    success: string,
  ) => {
    setPending(true);
    setError(null);
    try {
      replace(await api.updateProjectPublicApiSettings(workspaceId, projectId, operation));
      setAnnouncement(success);
      return true;
    } catch (cause) {
      if (cause instanceof AccountRequestError && cause.code === "PUBLIC_API_GENERATION_CONFLICT") {
        await load().catch(() => undefined);
        setError("This policy changed in another tab. Doorway refreshed the current generation.");
        setAnnouncement("The public API policy was refreshed after a stale-tab conflict.");
      } else {
        setError(accountErrorMessage(cause, "Doorway could not update the public API policy."));
        setAnnouncement("The public API policy change was not applied.");
      }
      return false;
    } finally {
      setPending(false);
    }
  };

  if (!settings && !error) return <p role="status">Loading public API policy...</p>;
  if (!settings || !limits) return (
    <div>
      <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>
      <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>Retry loading public API policy</button>
    </div>
  );

  const approvalAction = approvalActionId
    ? settings.actions.find((action) => action.id === approvalActionId) ?? null
    : null;
  const saveLimits = () => apply({
    operation: "set_limits",
    expectedAuthorityGeneration: settings.status.authorityGeneration,
    limits,
  }, "A new immutable public API policy version is active with the reviewed limits.");

  return (
    <section aria-labelledby={`public-api-policy-${projectId}`} className="review-stack">
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
      <div>
        <p className="step-label">Public API policy</p>
        <h3 id={`public-api-policy-${projectId}`}>Enabled actions and exact approval identity</h3>
        <p>Unknown and destructive actions stay protected. Mutation approval applies only to the identity shown here.</p>
      </div>
      {error ? <p id={errorId} ref={errorRef} tabIndex={-1} role="alert">{error}</p> : null}

      <div className="workspace-domain-sites">
        {settings.actions.map((action) => (
          <article key={action.id} aria-labelledby={`public-action-${action.id}`}>
            <h4 id={`public-action-${action.id}`}>{action.key}</h4>
            <p><strong>{riskLabel(action.risk)}</strong> · {action.publicEnabled ? "Public" : "Protected"}</p>
            <p>Approval: {approvalLabel(action.approvalStatus)}</p>
            {action.identity ? (
              <dl>
                <dt>Revision</dt><dd>{action.identity.revisionId}</dd>
                <dt>Action version</dt><dd>{action.identity.actionVersionId}</dd>
                <dt>Destination digest</dt><dd>{action.identity.destinationDigest}</dd>
                <dt>Request shape digest</dt><dd>{action.identity.requestShapeDigest}</dd>
                <dt>Connection generation</dt><dd>{action.identity.connectionGeneration}</dd>
                <dt>Credential version</dt><dd>{action.identity.credentialVersionId}</dd>
                <dt>Policy version</dt><dd>{action.identity.policyVersionId}</dd>
              </dl>
            ) : null}
            {action.risk === "mutation" && action.identity && settings.permissions.canApproveMutation ? (
              <button
                ref={approvalActionId === null ? approvalReturnRef : undefined}
                type="button"
                disabled={pending}
                onClick={(event) => {
                  approvalReturnRef.current = event.currentTarget;
                  setApprovalActionId(action.id);
                }}
              >Review and approve {action.key}</button>
            ) : null}
          </article>
        ))}
      </div>

      {approvalAction?.identity ? (
        <div role="alertdialog" aria-modal="true" aria-labelledby={`approve-${approvalAction.id}`}>
          <h4 id={`approve-${approvalAction.id}`}>Approve this exact mutation identity?</h4>
          <p>{approvalAction.key} will be public only for revision {approvalAction.identity.revisionId}, action version {approvalAction.identity.actionVersionId}, and credential version {approvalAction.identity.credentialVersionId}.</p>
          <button ref={approvalConfirmRef} type="button" disabled={pending} onClick={() => void apply({
            operation: "approve_mutation",
            expectedAuthorityGeneration: settings.status.authorityGeneration,
            actionPolicyId: approvalAction.id,
            identity: approvalAction.identity!,
          }, `${approvalAction.key} is approved for its exact current identity.`).then((ok) => {
            if (ok) setApprovalActionId(null);
            approvalReturnRef.current?.focus();
          })}>Approve exact mutation</button>
          <button type="button" onClick={() => {
            setApprovalActionId(null);
            approvalReturnRef.current?.focus();
          }}>Cancel</button>
        </div>
      ) : null}

      <section aria-labelledby={`public-limits-${projectId}`}>
        <h3 id={`public-limits-${projectId}`}>Usage and limits</h3>
        {settings.permissions.canLowerLimits ? (
          <div className="review-stack">
            {(Object.keys(limits) as Array<keyof PublicCredentialLimits>).map((key) => (
              <label key={key}>
                <span>{limitLabel(key)}</span>
                <input
                  type="number"
                  min={0}
                  max={settings.limits.ceilings[key]}
                  value={limits[key]}
                  disabled={pending}
                  onChange={(event) => setLimits((current) => current ? {
                    ...current,
                    [key]: Number(event.target.value),
                  } : current)}
                />
                <small>Platform ceiling {settings.limits.ceilings[key]}</small>
              </label>
            ))}
            <button type="button" disabled={pending} onClick={() => void saveLimits()}>Save as new policy version</button>
          </div>
        ) : <p>Only the workspace owner can change public API limits.</p>}
        {settings.usage.length ? (
          <table>
            <caption>Current UTC-day usage</caption>
            <thead><tr><th>Scope</th><th>Reads</th><th>Mutations</th></tr></thead>
            <tbody>{settings.usage.map((usage) => (
              <tr key={`${usage.scope}:${usage.scopeId}`}>
                <th>{usage.scope} · {usage.scopeId}</th>
                <td>{usage.readUsed} / {usage.readLimit}</td>
                <td>{usage.mutationUsed} / {usage.mutationLimit}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p>No public provider usage has been recorded today.</p>}
      </section>

      <section aria-labelledby={`public-signals-${projectId}`}>
        <h3 id={`public-signals-${projectId}`}>Provider signals</h3>
        {settings.signals.length ? <ul>{settings.signals.map((signal) => (
          <li key={`${signal.scope}:${signal.kind}:${signal.windowStartedAt}`}>
            {signal.kind}: {signal.count} / {signal.threshold}
          </li>
        ))}</ul> : <p>No active safety signals.</p>}
      </section>

      <section aria-labelledby={`public-audit-${projectId}`}>
        <h3 id={`public-audit-${projectId}`}>Sanitized audit history</h3>
        {settings.audit.length ? <ol>{settings.audit.map((event) => (
          <li key={event.id}>
            <strong>{event.reasonCode}</strong> · {event.actorKind} · {event.occurredAt}
            <span> Correlation {event.correlationId}</span>
          </li>
        ))}</ol> : <p>No public API control changes recorded.</p>}
      </section>
    </section>
  );
}

function riskLabel(risk: ProjectPublicApiSettings["actions"][number]["risk"]): string {
  return ({ read: "Read", mutation: "Mutation", destructive: "Destructive", unknown: "Unknown" } as const)[risk];
}

function approvalLabel(status: ProjectPublicApiSettings["actions"][number]["approvalStatus"]): string {
  return ({
    not_required: "No owner approval required",
    required: "Owner approval required",
    current: "Exact approval current",
    stale: "Approval stale",
    ineligible: "Ineligible for public access",
  } as const)[status];
}

function limitLabel(key: keyof PublicCredentialLimits): string {
  return ({
    readPerVisitorActionMinute: "Reads per visitor and action per minute",
    readPerProjectMinute: "Reads per project per minute",
    concurrentReadsPerProject: "Concurrent reads per project",
    mutationPerVisitorActionMinute: "Mutations per visitor and action per minute",
    mutationPerProjectMinute: "Mutations per project per minute",
    concurrentMutationsPerProject: "Concurrent mutations per project",
    dailyReadDispatchLimit: "Daily read dispatch limit",
    dailyMutationDispatchLimit: "Daily mutation dispatch limit",
  } as const)[key];
}
